import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faEnvelope,
  faFileCsv,
  faFileExcel,
  faFilter,
  faListAlt,
  faRotateRight,
  faSpinner,
  faSyncAlt,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { BASE_URL } from "../../config";

const statusOptions = ["pending", "queued", "sending", "sent", "failed", "skipped", "cancelled"];
const attemptStatusOptions = ["sending", "sent", "failed", "skipped"];
const recipientTypeOptions = ["provider", "physician", "fallback", "none"];
const bulkResendStatuses = ["pending", "queued", "failed", "skipped", "cancelled"];

const defaultSummary = {
  pending: 0,
  queued: 0,
  sending: 0,
  sent: 0,
  failed: 0,
  skipped: 0,
  cancelled: 0,
};

const defaultFilters = {
  date_from: "",
  date_to: "",
  status: "",
  attempt_status: "",
  recipient_type: "",
  settlement_id: "",
  payment_ref_no: "",
  upload_id: "",
  outbox_uuid: "",
  source: "",
  recipient_email: "",
  document_type: "",
  created_by: "",
};

const SettlementEmailOutboxPage = () => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [summary, setSummary] = useState(defaultSummary);
  const [filters, setFilters] = useState(defaultFilters);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [message, setMessage] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [isBulkResending, setIsBulkResending] = useState(false);
  const [attemptsModal, setAttemptsModal] = useState({
    open: false,
    outbox: null,
    rows: [],
    isLoading: false,
    page: 1,
    perPage: 10,
    totalPages: 1,
    totalRecords: 0,
    status: "",
    date_from: "",
    date_to: "",
  });

  const token = localStorage.getItem("authentication/token");

  const fetchQueue = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${BASE_URL}/provider-payment/settlement/email-outbox`, {
        params: {
          page,
          per_page: perPage,
          sort_by: sortBy,
          sort_direction: sortDir,
          ...filters,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const nextRows = response.data?.data || [];
      setRows(nextRows);
      setSelected((prev) =>
        prev.filter((id) => nextRows.some((row) => Number(row.id) === Number(id)))
      );
      setSummary(response.data?.summary || defaultSummary);
      const pagination = response.data?.pagination || {};
      setTotalPages(pagination.total_pages || 1);
      setTotalRecords(pagination.total_records || 0);
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "Failed to load email outbox.") });
    } finally {
      setIsLoading(false);
    }
  }, [filters, page, perPage, sortBy, sortDir, token]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const interval = setInterval(() => {
      fetchQueue();
    }, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchQueue]);

  const showingText = useMemo(() => {
    if (!totalRecords) return "No records";
    const from = (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, totalRecords);
    return `Showing ${from} to ${to} of ${totalRecords}`;
  }, [page, perPage, totalRecords]);

  const retryableRows = useMemo(
    () => rows.filter((row) => bulkResendStatuses.includes(row.status)),
    [rows]
  );
  const allRetryableSelected = retryableRows.length > 0
    && retryableRows.every((row) => selected.includes(Number(row.id)));

  const applyFilters = () => {
    setPage(1);
    fetchQueue();
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setPage(1);
  };

  const setFilterValue = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const setStatusFilter = (status) => {
    setFilters((prev) => ({ ...prev, status: prev.status === status ? "" : status }));
    setPage(1);
  };

  const toggleSelect = (outboxId) => {
    const id = Number(outboxId);
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (retryableRows.length === 0) return;
    if (allRetryableSelected) {
      setSelected([]);
      return;
    }
    setSelected(retryableRows.map((row) => Number(row.id)));
  };

  const resendSingle = async (row) => {
    if (row.status === "sending") return;
    if (row.status === "sent" && !window.confirm("This outbox row is already sent. Resend anyway?")) {
      return;
    }

    try {
      setResendingId(Number(row.id));
      setMessage(null);
      const response = await axios.post(
        `${BASE_URL}/provider-payment/settlement/email-outbox/resend/${row.id}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setMessage({
        type: "success",
        text: response.data?.message || "Email outbox row re-queued successfully.",
      });
      await fetchQueue();
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "Failed to resend email outbox row.") });
    } finally {
      setResendingId(null);
    }
  };

  const resendBulk = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Resend ${selected.length} selected email outbox record(s)?`)) return;

    try {
      setIsBulkResending(true);
      setMessage(null);
      const response = await axios.post(
        `${BASE_URL}/provider-payment/settlement/email-outbox/resend-bulk`,
        { outbox_ids: selected },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setMessage({
        type: "success",
        text: `${response.data?.count || 0} outbox row(s) re-queued. ${response.data?.skipped_count || 0} skipped.`,
      });
      setSelected([]);
      await fetchQueue();
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "Failed to bulk resend email outbox rows.") });
    } finally {
      setIsBulkResending(false);
    }
  };

  const exportQueue = async (format) => {
    try {
      const response = await axios.get(`${BASE_URL}/provider-payment/settlement/email-outbox/export`, {
        params: { format, ...filters },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: format === "excel" ? "application/vnd.ms-excel" : "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().replace(/[:T]/g, "-").split(".")[0];
      link.href = url;
      link.setAttribute(
        "download",
        `settlement_email_outbox_${timestamp}.${format === "excel" ? "xls" : "csv"}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setMessage({ type: "error", text: getErrorMessage(error, "Failed to export email outbox.") });
    }
  };

  const fetchAttempts = async (outboxId, nextPage = 1, overrides = {}) => {
    const nextStatus = overrides.status ?? attemptsModal.status;
    const nextDateFrom = overrides.date_from ?? attemptsModal.date_from;
    const nextDateTo = overrides.date_to ?? attemptsModal.date_to;

    try {
      setAttemptsModal((prev) => ({ ...prev, isLoading: true }));
      const response = await axios.get(
        `${BASE_URL}/provider-payment/settlement/email-outbox/${outboxId}/attempts`,
        {
          params: {
            page: nextPage,
            per_page: attemptsModal.perPage,
            status: nextStatus,
            date_from: nextDateFrom,
            date_to: nextDateTo,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const pagination = response.data?.pagination || {};
      setAttemptsModal((prev) => ({
        ...prev,
        open: true,
        outbox: response.data?.outbox || prev.outbox,
        rows: response.data?.data || [],
        isLoading: false,
        page: pagination.current_page || nextPage,
        totalPages: pagination.total_pages || 1,
        totalRecords: pagination.total_records || 0,
        status: nextStatus,
        date_from: nextDateFrom,
        date_to: nextDateTo,
      }));
    } catch (error) {
      setAttemptsModal((prev) => ({ ...prev, isLoading: false }));
      setMessage({ type: "error", text: getErrorMessage(error, "Failed to load outbox attempts.") });
    }
  };

  const openAttempts = (row) => {
    setAttemptsModal((prev) => ({
      ...prev,
      open: true,
      outbox: row,
      rows: [],
      page: 1,
      totalPages: 1,
      totalRecords: 0,
      status: "",
      date_from: "",
      date_to: "",
    }));
    fetchAttempts(row.id, 1, { status: "", date_from: "", date_to: "" });
  };

  const closeAttempts = () => {
    setAttemptsModal((prev) => ({ ...prev, open: false, outbox: null, rows: [] }));
  };

  const canBulkResend = (row) => bulkResendStatuses.includes(row.status);
  const canSingleResend = (row) => row.status !== "sending";

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settlement Email Outbox</h1>
          <p className="text-sm text-gray-600">PIS/Form 2307 notification queue</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={fetchQueue}
            className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <FontAwesomeIcon icon={faSyncAlt} className="mr-2" /> Refresh
          </button>
          <button
            onClick={() => exportQueue("csv")}
            className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <FontAwesomeIcon icon={faFileCsv} className="mr-2" /> CSV
          </button>
          <button
            onClick={() => exportQueue("excel")}
            className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <FontAwesomeIcon icon={faFileExcel} className="mr-2" /> Excel
          </button>
          <button
            onClick={resendBulk}
            disabled={selected.length === 0 || isBulkResending}
            className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={isBulkResending ? faSpinner : faRotateRight} spin={isBulkResending} className="mr-2" />
            Resend Selected
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`mb-4 rounded-lg border p-3 text-sm ${
            message.type === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-red-200 bg-red-50 text-red-700"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-4">
        {statusOptions.map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`bg-white border rounded-xl p-3 text-left hover:border-indigo-300 ${
              filters.status === status ? "border-indigo-500 ring-1 ring-indigo-200" : "border-gray-200"
            }`}
          >
            <div className="text-xs text-gray-500 capitalize">{status}</div>
            <div className={`text-2xl font-semibold ${statusTextClass(status)}`}>
              {summary[status] || 0}
            </div>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setAutoRefresh((prev) => !prev)}
          className={`px-3 py-2 text-sm rounded-lg border ${
            autoRefresh
              ? "bg-emerald-600 text-white border-emerald-600"
              : "bg-white border-gray-200"
          }`}
        >
          <FontAwesomeIcon icon={faClock} className="mr-2" />
          Auto-refresh {autoRefresh ? "On" : "Off"} (10s)
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <FilterInput label="Date From" type="date" value={filters.date_from} onChange={(value) => setFilterValue("date_from", value)} />
          <FilterInput label="Date To" type="date" value={filters.date_to} onChange={(value) => setFilterValue("date_to", value)} />
          <FilterSelect label="Status" value={filters.status} options={statusOptions} onChange={(value) => setFilterValue("status", value)} />
          <FilterSelect label="Latest Attempt" value={filters.attempt_status} options={attemptStatusOptions} onChange={(value) => setFilterValue("attempt_status", value)} />
          <FilterSelect label="Recipient Type" value={filters.recipient_type} options={recipientTypeOptions} onChange={(value) => setFilterValue("recipient_type", value)} />
          <FilterInput label="Settlement ID" value={filters.settlement_id} onChange={(value) => setFilterValue("settlement_id", value)} placeholder="Settlement id" />
          <FilterInput label="Payment Ref No" value={filters.payment_ref_no} onChange={(value) => setFilterValue("payment_ref_no", value)} placeholder="Payment ref no" />
          <FilterInput label="Upload ID" value={filters.upload_id} onChange={(value) => setFilterValue("upload_id", value)} placeholder="Upload id" />
          <FilterInput label="Outbox UUID" value={filters.outbox_uuid} onChange={(value) => setFilterValue("outbox_uuid", value)} placeholder="Outbox uuid" />
          <FilterInput label="Source" value={filters.source} onChange={(value) => setFilterValue("source", value)} placeholder="Source" />
          <FilterInput label="Recipient Email" value={filters.recipient_email} onChange={(value) => setFilterValue("recipient_email", value)} placeholder="Recipient, cc, or bcc" />
          <FilterInput label="Document Type" value={filters.document_type} onChange={(value) => setFilterValue("document_type", value)} placeholder="pis, form_2307" />
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={applyFilters}
            className="px-3 py-2 text-sm bg-gray-900 text-white rounded-lg"
          >
            <FontAwesomeIcon icon={faFilter} className="mr-2" /> Apply Filters
          </button>
          <button
            onClick={resetFilters}
            className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-3 text-sm text-gray-600 flex flex-wrap items-center justify-between gap-3">
          <span>{showingText}</span>
          <div className="flex items-center gap-2">
            <span>Sort</span>
            <select
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1"
            >
              <option value="created_at">Created</option>
              <option value="updated_at">Updated</option>
              <option value="status">Status</option>
              <option value="payment_ref_no">Payment Ref No</option>
              <option value="settlement_id">Settlement ID</option>
              <option value="source">Source</option>
              <option value="attempt_count">Attempts</option>
              <option value="sent_at">Sent At</option>
              <option value="failed_at">Failed At</option>
            </select>
            <select
              value={sortDir}
              onChange={(event) => setSortDir(event.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1"
            >
              <option value="desc">Desc</option>
              <option value="asc">Asc</option>
            </select>
            <span>Per page</span>
            <select
              value={perPage}
              onChange={(event) => {
                setPerPage(Number(event.target.value));
                setPage(1);
              }}
              className="border border-gray-200 rounded-lg px-2 py-1"
            >
              {[5, 10, 20, 50, 100].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="overflow-auto">
          <table className="min-w-[1450px] w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="p-3 text-left">
                  <input type="checkbox" checked={allRetryableSelected} onChange={selectAll} />
                </th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Outbox</th>
                <th className="p-3 text-left">Settlement</th>
                <th className="p-3 text-left">Source</th>
                <th className="p-3 text-left">Recipients</th>
                <th className="p-3 text-left">Documents</th>
                <th className="p-3 text-left">Attempts</th>
                <th className="p-3 text-left">Dates</th>
                <th className="p-3 text-left">Error</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading ? (
                <tr>
                  <td colSpan="11" className="p-4 text-center text-gray-500">
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="11" className="p-4 text-center text-gray-500">
                    No email outbox records found.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="align-top">
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selected.includes(Number(row.id))}
                        onChange={() => toggleSelect(row.id)}
                        disabled={!canBulkResend(row)}
                      />
                    </td>
                    <td className="p-3">
                      <span className={statusBadge(row.status)}>{row.status}</span>
                      {row.latest_attempt_status && (
                        <div className="mt-2 text-xs text-gray-500">
                          latest: <span className={statusTextClass(row.latest_attempt_status)}>{row.latest_attempt_status}</span>
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-gray-900">#{row.id}</div>
                      <div className="text-xs text-gray-500 break-all">{row.outbox_uuid}</div>
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-gray-900">{displayValue(row.payment_ref_no)}</div>
                      <div className="text-xs text-gray-500">Settlement ID: {displayValue(row.settlement_id)}</div>
                      <div className="text-xs text-gray-500">Upload: {displayValue(row.upload_id)}</div>
                      <div className="text-xs text-gray-500">SR: {displayValue(row.settlement_status)}</div>
                    </td>
                    <td className="p-3">
                      <div className="text-gray-900">{displayValue(row.source)}</div>
                      <div className="text-xs text-gray-500">{displayValue(row.recipient_type)}</div>
                      <div className="text-xs text-gray-500">
                        {[row.first_name, row.last_name].filter(Boolean).join(" ") || row.username || "-"}
                      </div>
                    </td>
                    <td className="p-3 max-w-xs">
                      <div className="text-xs text-gray-500">To</div>
                      <div className="break-words">{displayValue(row.recipient_emails)}</div>
                      {row.cc_emails && (
                        <>
                          <div className="text-xs text-gray-500 mt-2">CC</div>
                          <div className="break-words">{row.cc_emails}</div>
                        </>
                      )}
                    </td>
                    <td className="p-3">
                      <div>{displayValue(row.document_types)}</div>
                      <div className="text-xs text-gray-500">sent_mail_tag: {displayValue(row.sent_mail_tag)}</div>
                      <div className="text-xs text-gray-500">pis_mail: {displayValue(row.pis_mail_datetime)}</div>
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-gray-900">
                        {row.attempt_count || 0} / {row.max_attempts || 0}
                      </div>
                      <button
                        onClick={() => openAttempts(row)}
                        className="mt-2 px-2 py-1 text-xs bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <FontAwesomeIcon icon={faListAlt} className="mr-1" /> Attempts
                      </button>
                    </td>
                    <td className="p-3">
                      <DateLine label="Created" value={row.created_at} />
                      <DateLine label="Queued" value={row.queued_at} />
                      <DateLine label="Sent" value={row.sent_at} />
                      <DateLine label="Failed" value={row.failed_at} />
                    </td>
                    <td className="p-3 text-xs text-red-600 break-words max-w-xs">
                      {row.last_error || row.latest_attempt_error || "-"}
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => resendSingle(row)}
                        className="px-2 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                        disabled={!canSingleResend(row) || resendingId === Number(row.id)}
                      >
                        <FontAwesomeIcon
                          icon={resendingId === Number(row.id) ? faSpinner : faEnvelope}
                          spin={resendingId === Number(row.id)}
                          className="mr-1"
                        />
                        Resend
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="p-3 flex items-center justify-between text-sm text-gray-600">
          <button
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-50"
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {attemptsModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Outbox Attempts</h2>
                <p className="text-xs text-gray-500">
                  #{attemptsModal.outbox?.id} | {displayValue(attemptsModal.outbox?.payment_ref_no)} | {displayValue(attemptsModal.outbox?.source)}
                </p>
              </div>
              <button
                onClick={closeAttempts}
                className="h-9 w-9 rounded-lg border border-gray-200 hover:bg-gray-50"
                aria-label="Close attempts modal"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="p-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-gray-600">
                {attemptsModal.totalRecords ? `${attemptsModal.totalRecords} attempt(s)` : "No attempts"}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-600">From</span>
                <input
                  type="date"
                  value={attemptsModal.date_from}
                  onChange={(event) => fetchAttempts(attemptsModal.outbox?.id, 1, { date_from: event.target.value })}
                  className="border border-gray-200 rounded-lg px-2 py-1 text-sm"
                />
                <span className="text-sm text-gray-600">To</span>
                <input
                  type="date"
                  value={attemptsModal.date_to}
                  onChange={(event) => fetchAttempts(attemptsModal.outbox?.id, 1, { date_to: event.target.value })}
                  className="border border-gray-200 rounded-lg px-2 py-1 text-sm"
                />
                <span className="text-sm text-gray-600">Status</span>
                <select
                  value={attemptsModal.status}
                  onChange={(event) => fetchAttempts(attemptsModal.outbox?.id, 1, { status: event.target.value })}
                  className="border border-gray-200 rounded-lg px-2 py-1 text-sm"
                >
                  <option value="">All</option>
                  {attemptStatusOptions.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="p-3 text-left">Attempt</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Recipients</th>
                    <th className="p-3 text-left">Started</th>
                    <th className="p-3 text-left">Finished</th>
                    <th className="p-3 text-left">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {attemptsModal.isLoading ? (
                    <tr>
                      <td colSpan="6" className="p-4 text-center text-gray-500">
                        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                        Loading...
                      </td>
                    </tr>
                  ) : attemptsModal.rows.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="p-4 text-center text-gray-500">
                        No attempts found.
                      </td>
                    </tr>
                  ) : (
                    attemptsModal.rows.map((attempt) => (
                      <tr key={attempt.id} className="align-top">
                        <td className="p-3">
                          <div className="font-medium text-gray-900">#{attempt.attempt_no}</div>
                          <div className="text-xs text-gray-500">ID: {attempt.id}</div>
                        </td>
                        <td className="p-3">
                          <span className={statusBadge(attempt.status)}>{attempt.status}</span>
                        </td>
                        <td className="p-3 max-w-sm">
                          <div className="text-xs text-gray-500">To</div>
                          <div className="break-words">{displayValue(attempt.recipient_emails)}</div>
                          {attempt.cc_emails && (
                            <>
                              <div className="text-xs text-gray-500 mt-2">CC</div>
                              <div className="break-words">{attempt.cc_emails}</div>
                            </>
                          )}
                          {attempt.bcc_emails && (
                            <>
                              <div className="text-xs text-gray-500 mt-2">BCC</div>
                              <div className="break-words">{attempt.bcc_emails}</div>
                            </>
                          )}
                        </td>
                        <td className="p-3">{displayValue(attempt.started_at)}</td>
                        <td className="p-3">{displayValue(attempt.finished_at)}</td>
                        <td className="p-3 text-xs text-red-600 break-words max-w-sm">
                          {attempt.error_message || "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
              <button
                onClick={() => fetchAttempts(attemptsModal.outbox?.id, Math.max(1, attemptsModal.page - 1))}
                disabled={attemptsModal.page === 1}
                className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-50"
              >
                Previous
              </button>
              <span>Page {attemptsModal.page} of {attemptsModal.totalPages}</span>
              <button
                onClick={() => fetchAttempts(attemptsModal.outbox?.id, Math.min(attemptsModal.totalPages, attemptsModal.page + 1))}
                disabled={attemptsModal.page === attemptsModal.totalPages}
                className="px-3 py-1 rounded-md border border-gray-200 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FilterInput = ({ label, value, onChange, type = "text", placeholder = "" }) => (
  <div>
    <label className="text-xs text-gray-600">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
      placeholder={placeholder}
    />
  </div>
);

const FilterSelect = ({ label, value, options, onChange }) => (
  <div>
    <label className="text-xs text-gray-600">{label}</label>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
    >
      <option value="">All</option>
      {options.map((option) => (
        <option key={option} value={option}>{option}</option>
      ))}
    </select>
  </div>
);

const DateLine = ({ label, value }) => (
  <div className="text-xs">
    <span className="text-gray-500">{label}: </span>
    <span className="text-gray-700">{displayValue(value)}</span>
  </div>
);

const displayValue = (value) => {
  const normalized = value === null || value === undefined ? "" : String(value).trim();
  return normalized === "" ? "-" : normalized;
};

const statusBadge = (status) => {
  const base = "px-2 py-1 rounded-full text-xs capitalize";
  if (status === "sent") return `${base} bg-green-100 text-green-700`;
  if (status === "failed") return `${base} bg-red-100 text-red-700`;
  if (status === "sending") return `${base} bg-blue-100 text-blue-700`;
  if (status === "queued") return `${base} bg-yellow-100 text-yellow-700`;
  if (status === "skipped") return `${base} bg-gray-100 text-gray-700`;
  if (status === "cancelled") return `${base} bg-slate-200 text-slate-700`;
  return `${base} bg-slate-100 text-slate-700`;
};

const statusTextClass = (status) => {
  if (status === "sent") return "text-green-600";
  if (status === "failed") return "text-red-600";
  if (status === "sending") return "text-blue-600";
  if (status === "queued") return "text-yellow-600";
  if (status === "skipped") return "text-gray-600";
  if (status === "cancelled") return "text-slate-600";
  return "text-slate-600";
};

const getErrorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;
  if (typeof message === "string") return message;
  if (message && typeof message === "object") {
    return Object.values(message).flat().join(" ");
  }
  return fallback;
};

export default SettlementEmailOutboxPage;
